// ChairmanReview.js — Executive / Chairman read-only view
// Tab 1: Expense Reports  — all manager-submitted team expenses grouped by manager → employee → trip
// Tab 2: Audit Review     — all audit flags with full manager response + admin final action trail

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || "/api" });
API.interceptors.request.use(cfg => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtInr = n => parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const MONTHS_FULL = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`;
    opts.push({ val, label });
  }
  return opts;
}

// ── Audit status styles ────────────────────────────────────────────────────────
const STATUS_STYLE = {
  open:             { bg: "#FEF2F2", color: "#DC2626", border: "#FCA5A5", label: "Open"           },
  manager_reviewed: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE", label: "Mgr Responded"  },
  reviewed:         { bg: "#F0FDF4", color: "#16A34A", border: "#86EFAC", label: "Reviewed"       },
  escalated:        { bg: "#FFF7ED", color: "#EA580C", border: "#FED7AA", label: "Escalated"      },
  false_positive:   { bg: "#F5F3FF", color: "#7C3AED", border: "#C4B5FD", label: "False Positive" },
};
const SEV_STYLE = {
  high:   { bg: "#FEE2E2", color: "#B91C1C", label: "High"   },
  medium: { bg: "#FEF3C7", color: "#B45309", label: "Medium" },
  low:    { bg: "#F0FDF4", color: "#15803D", label: "Low"    },
};

const EXPLANATION_LABEL = {
  legitimate:       "✅ Legitimate",
  employee_error:   "⚠️ Employee Error",
  policy_exception: "📋 Policy Exception",
  suspicious:       "🚨 Suspicious",
};
const ACTION_LABEL = {
  no_action:        "No Action",
  requested_bills:  "Bills Requested",
  expense_adjusted: "Expense Adjusted",
  further_review:   "Further Review",
};

// ─────────────────────────────────────────────────────────────────────────────
//  TAB 1 — Expense Reports
// ─────────────────────────────────────────────────────────────────────────────
function ExpenseReportsTab() {
  const opts = monthOptions();
  const [month, setMonth] = useState(opts[0].val);
  const [company, setCompany] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/expense-report/chairman-view", {
        params: { month, company },
      });
      setRows(data);
    } catch (e) {
      console.error("chairman-view error:", e.response?.data || e.message || e);
      toast.error(e.response?.data?.error || e.message || "Failed to load expense data");
    } finally {
      setLoading(false);
    }
  }, [month, company]);

  useEffect(() => { load(); }, [load]);

  // Group: manager → employee → trip
  const grouped = {};
  for (const r of rows) {
    const mgrKey = r.manager_id ?? "unassigned";
    if (!grouped[mgrKey]) grouped[mgrKey] = {
      manager_id: r.manager_id,
      manager_name: r.manager_name || "No Manager",
      manager_email: r.manager_email || "",
      manager_company: r.manager_company || "",
      employees: {},
    };
    const empKey = r.employee_id;
    if (!grouped[mgrKey].employees[empKey]) grouped[mgrKey].employees[empKey] = {
      employee_id: r.employee_id,
      employee_name: r.employee_name,
      employee_email: r.employee_email,
      department: r.department,
      trips: {},
    };
    const tripKey = r.trip_id;
    if (!grouped[mgrKey].employees[empKey].trips[tripKey]) grouped[mgrKey].employees[empKey].trips[tripKey] = {
      trip_id: r.trip_id,
      trip_title: r.trip_title,
      destination: r.destination,
      advance_amount: parseFloat(r.advance_amount || 0),
      total_expenses: parseFloat(r.total_expenses || 0),
      trip_status: r.trip_status,
      expenses: [],
    };
    if (r.expense_id) {
      grouped[mgrKey].employees[empKey].trips[tripKey].expenses.push({
        expense_id: r.expense_id,
        expense_date: r.expense_date,
        category: r.category,
        food_subcategory: r.food_subcategory,
        description: r.description,
        amount: parseFloat(r.amount || 0),
        bill_type: r.bill_type,
      });
    }
  }

  const managers = Object.values(grouped);

  // Grand totals
  const grandAdvance = rows.reduce((s, r) => s, 0);
  const grandSpent = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);

  // Per-manager totals
  function mgrTotals(mgr) {
    let advance = 0, spent = 0, empCount = 0;
    for (const emp of Object.values(mgr.employees)) {
      empCount++;
      for (const trip of Object.values(emp.trips)) {
        advance += trip.advance_amount;
        spent += trip.total_expenses;
      }
    }
    return { advance, spent, empCount };
  }

  const companyColor = { ebek: "#B71C1C", ebekmed: "#0F6E56", "": "#4B5563" };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <select
          value={month}
          onChange={e => setMonth(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, background: "white" }}
        >
          {opts.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
        </select>
        <select
          value={company}
          onChange={e => setCompany(e.target.value)}
          style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, background: "white" }}
        >
          <option value="all">All Companies</option>
          <option value="ebek">Ebek</option>
          <option value="ebekmed">Ebek Med</option>
        </select>
        <button
          onClick={load}
          style={{ padding: "8px 16px", background: "#1D4ED8", color: "white", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}
        >
          🔄 Refresh
        </button>
        <div style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280" }}>
          {rows.length} expense records · {managers.length} manager{managers.length !== 1 ? "s" : ""}
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280", fontSize: 15 }}>
          Loading expense data…
        </div>
      )}

      {!loading && managers.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af", fontSize: 15 }}>
          No expense data for {opts.find(o => o.val === month)?.label}.
        </div>
      )}

      {!loading && managers.map(mgr => {
        const { advance, spent, empCount } = mgrTotals(mgr);
        const diff = spent - advance;
        const accentColor = companyColor[mgr.manager_company] || "#374151";

        return (
          <div key={mgr.manager_id} style={{ marginBottom: 28, border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
            {/* Manager header */}
            <div style={{ background: accentColor, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ color: "white", fontWeight: 700, fontSize: 16 }}>
                  👤 {mgr.manager_name}
                </div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, marginTop: 2 }}>
                  {mgr.manager_email} &nbsp;·&nbsp; {empCount} employee{empCount !== 1 ? "s" : ""}
                  {mgr.manager_company && ` · ${mgr.manager_company === "ebekmed" ? "Ebek Med" : "Ebek"}`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                {[
                  { label: "Advance", val: `₹${fmtInr(advance)}`, color: "rgba(255,255,255,0.9)" },
                  { label: "Spent",   val: `₹${fmtInr(spent)}`,   color: diff > 0 ? "#FCA5A5" : "#86EFAC" },
                  { label: diff > 0 ? "Over" : "Under", val: `₹${fmtInr(Math.abs(diff))}`, color: diff > 0 ? "#FCA5A5" : "#86EFAC" },
                ].map(kpi => (
                  <div key={kpi.label} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{kpi.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: kpi.color }}>{kpi.val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Employees */}
            <div style={{ padding: "0 20px 20px" }}>
              {Object.values(mgr.employees).map(emp => {
                const empAdvance = Object.values(emp.trips).reduce((s, t) => s + t.advance_amount, 0);
                const empSpent   = Object.values(emp.trips).reduce((s, t) => s + t.total_expenses, 0);
                const empDiff    = empSpent - empAdvance;

                return (
                  <div key={emp.employee_id} style={{ marginTop: 16, border: "1px solid #f3f4f6", borderRadius: 8, overflow: "hidden" }}>
                    {/* Employee sub-header */}
                    <div style={{ background: "#f9fafb", padding: "10px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>👤 {emp.employee_name}</span>
                        {emp.department && <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>{emp.department}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        Advance: <strong>₹{fmtInr(empAdvance)}</strong>
                        &nbsp;·&nbsp; Spent: <strong>₹{fmtInr(empSpent)}</strong>
                        &nbsp;·&nbsp;
                        <span style={{ color: empDiff > 0 ? "#DC2626" : "#16A34A", fontWeight: 700 }}>
                          {empDiff > 0 ? "Over" : "Under"} ₹{fmtInr(Math.abs(empDiff))}
                        </span>
                      </div>
                    </div>

                    {/* Trips */}
                    <div style={{ padding: "12px 16px" }}>
                      {Object.values(emp.trips).map(trip => (
                        <div key={trip.trip_id} style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", padding: "7px 10px", background: "#f3f4f6", borderRadius: 6, borderLeft: `3px solid ${accentColor}`, marginBottom: 8 }}>
                            ✈️ {trip.trip_title}
                            {trip.destination && <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}>· 📍 {trip.destination}</span>}
                            <span style={{ float: "right", fontSize: 12, fontWeight: 400, color: "#6b7280" }}>
                              Advance ₹{fmtInr(trip.advance_amount)} · Spent ₹{fmtInr(trip.total_expenses)}
                            </span>
                          </div>
                          {trip.expenses.length > 0 ? (
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: "#f9fafb" }}>
                                  {["Date","Category","Amount","Bill Type","Description"].map(h => (
                                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {trip.expenses.map((ex, i) => {
                                  const hiAmt = ex.amount >= 5000;
                                  return (
                                    <tr key={ex.expense_id} style={{ background: i % 2 === 0 ? "white" : "#f9fafb" }}>
                                      <td style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", color: "#374151", whiteSpace: "nowrap" }}>
                                        {new Date(ex.expense_date).toLocaleDateString("en-IN")}
                                      </td>
                                      <td style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", color: "#374151" }}>
                                        {ex.category}
                                        {ex.food_subcategory && <span style={{ color: "#9ca3af", fontSize: 11 }}> / {ex.food_subcategory}</span>}
                                      </td>
                                      <td style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", fontWeight: 700, color: hiAmt ? "#DC2626" : "#1f2937", whiteSpace: "nowrap" }}>
                                        ₹{fmtInr(ex.amount)}
                                      </td>
                                      <td style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", color: "#6b7280", fontSize: 12 }}>
                                        {ex.bill_type || "—"}
                                      </td>
                                      <td style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", color: "#6b7280", fontStyle: "italic", fontSize: 12 }}>
                                        {ex.description || "—"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ fontSize: 12, color: "#9ca3af", padding: "6px 10px" }}>No individual expenses recorded for this trip in the selected month.</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TAB 2 — Audit Review (read-only)
// ─────────────────────────────────────────────────────────────────────────────

function AuditFlagCard({ flag }) {
  const sstyle  = STATUS_STYLE[flag.status] || STATUS_STYLE.open;
  const sevstyle = SEV_STYLE[flag.severity] || SEV_STYLE.low;

  return (
    <div style={{ border: `1px solid ${sstyle.border}`, borderRadius: 10, marginBottom: 14, background: "white", overflow: "hidden" }}>
      {/* Card header */}
      <div style={{ padding: "12px 16px", background: sstyle.bg, borderBottom: `1px solid ${sstyle.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937", marginBottom: 4 }}>{flag.flag_type}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            👤 {flag.employee_name}
            {flag.employee_company && <span style={{ marginLeft: 6 }}>· {flag.employee_company}</span>}
            {flag.trip_title && <span style={{ marginLeft: 6 }}>· ✈️ {flag.trip_title}</span>}
            {flag.trip_destination && <span style={{ marginLeft: 4 }}>📍 {flag.trip_destination}</span>}
          </div>
          {flag.description && (
            <div style={{ fontSize: 12, color: "#374151", marginTop: 4, lineHeight: 1.5 }}>{flag.description}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: sevstyle.bg, color: sevstyle.color }}>
            {sevstyle.label}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: sstyle.bg, color: sstyle.color, border: `1px solid ${sstyle.border}` }}>
            {sstyle.label}
          </span>
        </div>
      </div>

      {/* Expense detail */}
      {(flag.expense_amount || flag.expense_category) && (
        <div style={{ padding: "8px 16px", background: "#fafafa", borderBottom: "1px solid #f3f4f6", fontSize: 12, color: "#374151" }}>
          {flag.expense_date && <span>📅 {new Date(flag.expense_date).toLocaleDateString("en-IN")} &nbsp;·&nbsp;</span>}
          {flag.expense_category && <span>{flag.expense_category} &nbsp;·&nbsp;</span>}
          {flag.expense_amount && <span style={{ fontWeight: 700, color: "#DC2626" }}>₹{fmtInr(flag.expense_amount)}</span>}
        </div>
      )}

      {/* Manager response panel */}
      {flag.manager_justification && (
        <div style={{ padding: "12px 16px", background: "#EFF6FF", borderBottom: "1px solid #BFDBFE" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            📝 Manager Response
            {flag.manager_response_at && (
              <span style={{ fontWeight: 400, marginLeft: 8, color: "#6b7280" }}>
                {new Date(flag.manager_response_at).toLocaleDateString("en-IN")}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: flag.manager_verified ? "#D1FAE5" : "#FEF3C7", color: flag.manager_verified ? "#065F46" : "#92400E" }}>
              {flag.manager_verified ? "✓ Verified with Employee" : "⚠ Not Verified"}
            </span>
            {flag.manager_explanation && (
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#DBEAFE", color: "#1D4ED8" }}>
                {EXPLANATION_LABEL[flag.manager_explanation] || flag.manager_explanation}
              </span>
            )}
            {flag.manager_action_taken && (
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#F3F4F6", color: "#374151" }}>
                🔧 {ACTION_LABEL[flag.manager_action_taken] || flag.manager_action_taken}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#1e40af", fontStyle: "italic", lineHeight: 1.6 }}>
            "{flag.manager_justification}"
          </div>
        </div>
      )}

      {/* Admin / final review panel */}
      {["reviewed", "escalated", "false_positive"].includes(flag.status) && flag.reviewed_by_name && (
        <div style={{ padding: "12px 16px", background: flag.status === "escalated" ? "#FFF7ED" : flag.status === "false_positive" ? "#F5F3FF" : "#F0FDF4", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: flag.status === "escalated" ? "#EA580C" : flag.status === "false_positive" ? "#7C3AED" : "#16A34A", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
            {flag.status === "escalated" ? "🔺 Escalated" : flag.status === "false_positive" ? "🟣 Marked False Positive" : "✅ Admin Reviewed"}
            &nbsp;by {flag.reviewed_by_name}
            {flag.reviewed_at && <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}>{new Date(flag.reviewed_at).toLocaleDateString("en-IN")}</span>}
          </div>
          {flag.review_notes && (
            <div style={{ fontSize: 13, color: "#374151", fontStyle: "italic", lineHeight: 1.6 }}>
              "{flag.review_notes}"
            </div>
          )}
        </div>
      )}

      {/* Footer timestamps */}
      <div style={{ padding: "8px 16px", fontSize: 11, color: "#9ca3af", display: "flex", justifyContent: "space-between" }}>
        <span>Detected {new Date(flag.detected_at).toLocaleDateString("en-IN")}</span>
        {flag.bill_type && <span>Bill: {flag.bill_type}</span>}
      </div>
    </div>
  );
}

function AuditReviewTab() {
  const [flags, setFlags] = useState([]);
  const [summary, setSummary] = useState({ total: 0, open: {}, manager_reviewed: {}, reviewed: {}, escalated: {}, false_positive: {} });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [company, setCompany] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 30;

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await API.get("/audit/flags/summary", { params: { company: company !== "all" ? company : undefined } });
      setSummary(data);
    } catch (e) { /* silent */ }
  }, [company]);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/audit/flags", {
        params: {
          status:  statusFilter !== "all" ? statusFilter : undefined,
          company: company !== "all" ? company : undefined,
          page,
          limit: LIMIT,
        },
      });
      setFlags(data.flags || []);
      setTotalPages(data.pages || 1);
    } catch (e) {
      console.error("audit-flags error:", e.response?.data || e.message || e);
      toast.error(e.response?.data?.error || e.message || "Failed to load audit flags");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, company, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setPage(1); }, [statusFilter, company]);
  useEffect(() => { loadFlags(); }, [loadFlags]);

  const summaryCards = [
    { key: "all",             label: "All Flags",     total: summary.total,                        icon: "🔍", color: "#374151" },
    { key: "open",            label: "Open",          total: summary.open?.total || 0,             icon: "🔴", color: "#DC2626" },
    { key: "manager_reviewed",label: "Mgr Responded", total: summary.manager_reviewed?.total || 0, icon: "📝", color: "#1D4ED8" },
    { key: "escalated",       label: "Escalated",     total: summary.escalated?.total || 0,        icon: "🟠", color: "#EA580C" },
    { key: "reviewed",        label: "Reviewed",      total: summary.reviewed?.total || 0,         icon: "✅", color: "#16A34A" },
    { key: "false_positive",  label: "False Positive",total: summary.false_positive?.total || 0,   icon: "🟣", color: "#7C3AED" },
  ];

  return (
    <div>
      {/* Summary bar */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {summaryCards.map(card => (
          <button
            key={card.key}
            onClick={() => setStatusFilter(card.key)}
            style={{
              flex: "1 1 100px", minWidth: 100, padding: "10px 8px", borderRadius: 10,
              border: statusFilter === card.key ? `2px solid ${card.color}` : "2px solid #e5e7eb",
              background: statusFilter === card.key ? "#f9fafb" : "white",
              cursor: "pointer", textAlign: "center",
            }}
          >
            <div style={{ fontSize: 13 }}>{card.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: card.color }}>{card.total}</div>
            <div style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{card.label}</div>
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <select
          value={company}
          onChange={e => setCompany(e.target.value)}
          style={{ padding: "7px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, background: "white" }}
        >
          <option value="all">All Companies</option>
          <option value="ebek">Ebek</option>
          <option value="ebekmed">Ebek Med</option>
        </select>
        <button
          onClick={() => { loadSummary(); loadFlags(); }}
          style={{ padding: "7px 14px", background: "#1D4ED8", color: "white", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}
        >
          🔄 Refresh
        </button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          {flags.length} of {summary.total} flags
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280", fontSize: 15 }}>
          Loading audit flags…
        </div>
      )}

      {!loading && flags.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af", fontSize: 15 }}>
          No audit flags found for the selected filters.
        </div>
      )}

      {!loading && flags.map(flag => (
        <AuditFlagCard key={flag.id} flag={flag} />
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            style={{ padding: "7px 14px", border: "1px solid #d1d5db", borderRadius: 8, background: page <= 1 ? "#f9fafb" : "white", cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: 13 }}
          >
            ← Prev
          </button>
          <span style={{ padding: "7px 14px", fontSize: 13, color: "#374151" }}>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            style={{ padding: "7px 14px", border: "1px solid #d1d5db", borderRadius: 8, background: page >= totalPages ? "#f9fafb" : "white", cursor: page >= totalPages ? "not-allowed" : "pointer", fontSize: 13 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main ChairmanReview page
// ─────────────────────────────────────────────────────────────────────────────
export default function ChairmanReview() {
  const [activeTab, setActiveTab] = useState("expenses");

  const tabs = [
    { key: "expenses", icon: "📋", label: "Expense Reports" },
    { key: "audit",    icon: "🔍", label: "Audit Review"    },
  ];

  return (
    <div style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
      {/* Page heading */}
      <div style={{ marginBottom: 24, background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)", borderRadius: 12, padding: "20px 24px" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", margin: 0 }}>👑 Chairman Review</h1>
        <p style={{ fontSize: 13, color: "#bfdbfe", marginTop: 4, marginBottom: 0 }}>
          Read-only overview of all team expense reports and audit activity across both companies.
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e5e7eb", marginBottom: 24 }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 20px",
              border: "none",
              borderBottom: activeTab === tab.key ? "3px solid #1D4ED8" : "3px solid transparent",
              background: "transparent",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 700 : 400,
              color: activeTab === tab.key ? "#1D4ED8" : "#6b7280",
              marginBottom: -2,
              transition: "all 0.15s",
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "expenses" && <ExpenseReportsTab />}
      {activeTab === "audit"    && <AuditReviewTab />}
    </div>
  );
}
